import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setEditing,
  setEntryLockData,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
/**
 * Custom hook to manage entry locks for logbook entries.
 *
 * @returns A set of functions and state to handle entry lock operations.
 */
export const useEntryLock = () => {
  const { post } = usePostApi();
  const { idType } = useParams();
  const locked24 = useSelector(state => state.generatorSlice.locked24);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const {
    current,
    editing,
    activeAutosave,
    releaseEntryLock,
    locked,
    you,
    openedRefetchModal,
  } = useSelector(state => state.entrySlice);

  const isNewEntry = useIsNewEntry();
  const dispatch = useDispatch();
  const idObject = useMemo(
    () => ({ [String(idType)]: isNewEntry ? "new" : id }),
    [idType, isNewEntry, id],
  );
  const keepAliveIntervalsRef = useRef<NodeJS.Timeout[]>([]);

  const openedRefetchModalRef = useRef(openedRefetchModal);

  useEffect(() => {
    openedRefetchModalRef.current = openedRefetchModal;
  }, [openedRefetchModal]);

  /**
   * Checks the current lock status of the entry.
   *
   * @callback handleCheckLock
   * @param idObject - An object containing the entry ID and type.
   * @param current - The current timestamp or identifier for the entry.
   */
  const handleCheckLock = useCallback(() => {
    if (!window.navigator.onLine) return;
    void post({
      request: "check-entry-lock",
      body: { params: { ...idObject, current } },
      onSuccess: data =>
        dispatch(
          setEntryLockData({
            ...data,
            activeAutosave: false,
          }),
        ),
    });
  }, [current, dispatch, idObject, post]);
  /**
   * Acquires a lock for the entry.
   *
   * @callback handleGetLock
   * @param idObject - An object containing the entry ID and type.
   * @param current - The current timestamp or identifier for the entry.
   */
  const handleGetLock = useCallback(() => {
    if (!window.navigator.onLine) return;

    if (Object.values(idObject)[0] !== "new") {
      void post({
        request: "get-entry-lock",
        body: { params: { ...idObject, current } },
        onSuccess: data => {
          dispatch(
            setEntryLockData({
              ...data,
              activeAutosave: data.locked && data.you,
              lockedByOther: data.locked && !data.you,
            }),
          );
        },
      });
    } else {
      dispatch(
        setEntryLockData({
          locked: true,
          user: null,
          you: true,
          changed: 0,
          activeAutosave: true,
        }),
      );
    }
  }, [current, dispatch, idObject, post]);
  /**
   * Sends a keep-alive signal to maintain the entry lock.
   *
   * @callback handleKeepAlive
   * @param idObject - An object containing the entry ID and type.
   */
  const handleKeepAlive = useCallback(() => {
    if (!window.navigator.onLine) return;
    void post({
      request: "keep-alive-entry-lock",
      body: { params: { ...idObject } },
      onError: () => {
        void handleGetLock();
      },
    });
  }, [idObject, post, handleGetLock]);
  /**
   * Releases the entry lock on the server.
   *
   * @callback handleReleaseEntryLock
   * @param idObject - An object containing the entry ID and type.
   */
  const handleReleaseEntryLock = useCallback(() => {
    if (!window.navigator.onLine) return;
    void post({
      request: "release-entry-lock",
      body: { params: { ...idObject } },
    });
    dispatch(setEntryLockData({ locked: false, you: false, user: null }));
  }, [dispatch, idObject, post]);

  const releaseLock = useCallback(() => {
    dispatch(setReleaseEntryLock(0));
    keepAliveIntervalsRef.current.forEach(interval => clearInterval(interval));
    keepAliveIntervalsRef.current = [];
    void handleReleaseEntryLock();
  }, [dispatch, handleReleaseEntryLock]);

  useEffect(() => {
    let checkLockInterval: NodeJS.Timeout;
    let keepAliveLocalInt: NodeJS.Timeout;

    if (locked24) return;
    if (locked && !you) {
      dispatch(setEditing(false));
    }

    // Only check lock for existing entries when we have current and a valid id value
    const currentEntryId = Object.values(idObject)[0];
    const canCheckLock =
      Boolean(current) && Boolean(currentEntryId) && currentEntryId !== "new";

    if (!editing && (!locked || (locked && !you))) {
      if (canCheckLock) {
        void handleCheckLock();
        checkLockInterval = setInterval(() => {
          void handleCheckLock();
        }, 3000);
      }
    }

    // Only get/keep lock when editing is active
    if (editing) {
      if (activeAutosave && locked && you) {
        void handleKeepAlive();
        keepAliveLocalInt = setInterval(() => {
          if (!openedRefetchModalRef.current) {
            // ensure keeping alive only runs when in entry
            if (!window.location.href.includes("logbook")) {
              clearInterval(keepAliveLocalInt);
              void handleReleaseEntryLock();
              return;
            }
            void handleKeepAlive();
          }
        }, 5000);
        keepAliveIntervalsRef.current.push(keepAliveLocalInt);
      } else if (!locked) {
        // Only get lock when editing starts and not locked
        void handleGetLock();
      }
    }

    return () => {
      clearInterval(checkLockInterval);
      clearInterval(keepAliveLocalInt);
      keepAliveIntervalsRef.current.forEach(interval =>
        clearInterval(interval),
      );
      keepAliveIntervalsRef.current = [];
      if (!window.location.href.includes("logbook")) {
        void handleReleaseEntryLock();
      }
    };
  }, [
    activeAutosave,
    dispatch,
    editing,
    handleCheckLock,
    handleGetLock,
    handleKeepAlive,
    handleReleaseEntryLock,
    locked24,
    locked,
    you,
    openedRefetchModal,
    idObject,
    current,
  ]);

  useEffect(() => {
    if (releaseEntryLock !== 0) {
      releaseLock();
      dispatch(setEditing(false));
    }
  }, [dispatch, releaseEntryLock, releaseLock]);
};
